/**
 * [AXE 6] MfaContext.js
 * Manages administrative 2FA (TOTP) state, session storage persistence, and modal controls.
 *
 * ARCHITECTURAL RULE:
 * MfaContext and ProtectedRoute are STRICTLY UX mechanisms (handling prompt modals, view toggles,
 * and page transitions). Real security is enforced 100% server-side in Cloud Functions via
 * Firebase ID Token + X-MFA-Session (and X-MFA-Fresh for critical operations).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getAuth, onIdTokenChanged } from "firebase/auth";
import { getFunctionsUrl } from "../config/FirebaseConfig";

const MfaContext = createContext(null);

const STORAGE_SESSION_ID_KEY = "mfa_session_id";
const STORAGE_EXPIRES_AT_KEY = "mfa_session_expires_at";

const getFunctionsBaseUrl = () => {
  return getFunctionsUrl();
};

export const MfaProvider = ({ children }) => {
  // Read initial session state from sessionStorage (persists across F5, isolated per tab, destroyed on tab close)
  const [mfaSessionId, setMfaSessionId] = useState(() => {
    try {
      const storedId = sessionStorage.getItem(STORAGE_SESSION_ID_KEY);
      const storedExp = sessionStorage.getItem(STORAGE_EXPIRES_AT_KEY);
      if (storedId && storedExp && Number(storedExp) > Date.now()) {
        return storedId;
      }
      // Clean up stale session
      sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
      sessionStorage.removeItem(STORAGE_EXPIRES_AT_KEY);
      return null;
    } catch (_) {
      return null;
    }
  });

  const [mfaSessionExpiresAt, setMfaSessionExpiresAt] = useState(() => {
    try {
      const storedExp = sessionStorage.getItem(STORAGE_EXPIRES_AT_KEY);
      return storedExp ? Number(storedExp) : null;
    } catch (_) {
      return null;
    }
  });

  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(() => Boolean(mfaSessionId));
  const [showMfaModal, setShowMfaModal] = useState(false);

  // Fresh MFA modal states (critical actions)
  const [showFreshModal, setShowFreshModal] = useState(false);
  const [freshActionDescription, setFreshActionDescription] = useState("");
  const freshActionResolverRef = useRef(null);

  /**
   * Clear client MFA session (called on signOff or session expiry)
   */
  const clearMfaSession = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
      sessionStorage.removeItem(STORAGE_EXPIRES_AT_KEY);
    } catch (_) {}
    setMfaSessionId(null);
    setMfaSessionExpiresAt(null);
    setMfaVerified(false);
  }, []);

  /**
   * Save a newly verified MFA session to sessionStorage & React state
   */
  const saveMfaSession = useCallback((sessionId, expiresAt) => {
    try {
      sessionStorage.setItem(STORAGE_SESSION_ID_KEY, sessionId);
      sessionStorage.setItem(STORAGE_EXPIRES_AT_KEY, String(expiresAt));
    } catch (_) {}
    setMfaSessionId(sessionId);
    setMfaSessionExpiresAt(expiresAt);
    setMfaVerified(true);
    setShowMfaModal(false);
  }, []);

  /**
   * Returns headers to attach to admin API requests
   */
  const getMfaHeaders = useCallback(() => {
    if (mfaSessionId && mfaSessionExpiresAt && mfaSessionExpiresAt > Date.now()) {
      return { "X-MFA-Session": mfaSessionId };
    }
    try {
      const storedId = sessionStorage.getItem(STORAGE_SESSION_ID_KEY);
      const storedExp = sessionStorage.getItem(STORAGE_EXPIRES_AT_KEY);
      if (storedId && storedExp && Number(storedExp) > Date.now()) {
        return { "X-MFA-Session": storedId };
      }
    } catch (_) {}
    return {};
  }, [mfaSessionId, mfaSessionExpiresAt]);

  /**
   * Listen to Firebase Auth state & Custom Claims to detect mfa_enrolled
   */
  useEffect(() => {
    let unsubscribe;
    try {
      const auth = getAuth();
      unsubscribe = onIdTokenChanged(auth, async (user) => {
        if (!user) {
          clearMfaSession();
          setMfaEnrolled(false);
          setShowMfaModal(false);
          return;
        }

        try {
          const tokenResult = await user.getIdTokenResult();
          const enrolled = tokenResult.claims.mfa_enrolled === true;
          setMfaEnrolled(enrolled);

          // If user has 2FA enrolled
          if (enrolled) {
            const currentSession = sessionStorage.getItem(STORAGE_SESSION_ID_KEY);
            const currentExp = sessionStorage.getItem(STORAGE_EXPIRES_AT_KEY);
            const isValid = currentSession && currentExp && Number(currentExp) > Date.now();

            if (isValid) {
              setMfaSessionId(currentSession);
              setMfaSessionExpiresAt(Number(currentExp));
              setMfaVerified(true);
              setShowMfaModal(false);
            } else {
              clearMfaSession();
              // Do not force show modal on unauthenticated routes, ProtectedRoute handles route gating
            }
          } else {
            // Not enrolled
            clearMfaSession();
          }
        } catch (err) {
          console.error("[MfaContext] Error reading ID token claims:", err);
        }
      });
    } catch (e) {
      console.warn("[MfaContext] Auth listener initialization skipped:", e.message);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [clearMfaSession]);

  /**
   * Periodic expiration check for active session
   */
  useEffect(() => {
    if (!mfaSessionExpiresAt) return;

    const remainingTime = mfaSessionExpiresAt - Date.now();
    if (remainingTime <= 0) {
      clearMfaSession();
      if (mfaEnrolled) setShowMfaModal(true);
      return;
    }

    const timer = setTimeout(() => {
      clearMfaSession();
      if (mfaEnrolled) setShowMfaModal(true);
    }, remainingTime);

    return () => clearTimeout(timer);
  }, [mfaSessionExpiresAt, mfaEnrolled, clearMfaSession]);

  /**
   * Verify TOTP code or Backup Code (Daily login)
   * @param {string} code - 6-digit TOTP code OR 12-char backup code
   * @param {boolean} isBackup - true if submitting a backup code
   */
  const verifyMfa = useCallback(
    async (code, isBackup = false) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Utilisateur non connecté.");
      }

      const idToken = await user.getIdToken(true);
      const url = `${getFunctionsBaseUrl()}/admin_2fa_verify`;

      const payload = isBackup
        ? { backupCode: code.trim() }
        : { code: code.trim() };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || "Échec de la vérification.");
      }

      saveMfaSession(data.sessionId, data.expiresAt);
      return data;
    },
    [saveMfaSession]
  );

  /**
   * Request Fresh MFA token for critical action.
   * Returns a promise that resolves with the freshToken once user validates the modal.
   */
  const requestFreshMfa = useCallback((description = "") => {
    return new Promise((resolve, reject) => {
      setFreshActionDescription(description);
      setShowFreshModal(true);
      freshActionResolverRef.current = { resolve, reject };
    });
  }, []);

  /**
   * Verify fresh TOTP code during critical action
   */
  const verifyFreshMfa = useCallback(
    async (code) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté.");

      const idToken = await user.getIdToken(true);
      const url = `${getFunctionsBaseUrl()}/admin_2fa_verify_fresh`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
          ...getMfaHeaders(),
        },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || "Code invalide.");
      }

      setShowFreshModal(false);
      if (freshActionResolverRef.current) {
        freshActionResolverRef.current.resolve(data.freshToken);
        freshActionResolverRef.current = null;
      }
      return data.freshToken;
    },
    [getMfaHeaders]
  );

  /**
   * Cancel fresh MFA modal
   */
  const cancelFreshMfa = useCallback(() => {
    setShowFreshModal(false);
    if (freshActionResolverRef.current) {
      freshActionResolverRef.current.reject(new Error("Action annulée par l'utilisateur."));
      freshActionResolverRef.current = null;
    }
  }, []);

  const value = {
    mfaEnrolled,
    setMfaEnrolled,
    mfaVerified,
    mfaSessionId,
    mfaSessionExpiresAt,
    showMfaModal,
    setShowMfaModal,
    showFreshModal,
    freshActionDescription,
    verifyMfa,
    requestFreshMfa,
    verifyFreshMfa,
    cancelFreshMfa,
    clearMfaSession,
    saveMfaSession,
    getMfaHeaders,
  };

  return <MfaContext.Provider value={value}>{children}</MfaContext.Provider>;
};

export const useMfa = () => {
  const context = useContext(MfaContext);
  if (!context) {
    throw new Error("useMfa must be used within an MfaProvider");
  }
  return context;
};

export default MfaContext;
